import { getStore } from '../db/stores.js';
import { getAislesByStore } from '../db/aisles.js';
import { getListItems, toggleItem } from '../db/articles.js';
import { renderHeader } from '../components/header.js';
import { createElement, clearElement, showToast } from '../utils/dom.js';
import { navigate } from '../router.js';
import {
  iconMapPin,
  iconPackage,
  iconCheckCircle,
  iconShoppingBag,
  iconCheck,
} from '../utils/icons.js';

/**
 * Affiche le mode courses : vue optimisée pour faire ses courses en magasin.
 * Articles regroupés par rayon dans l'ordre configuré, avec progression.
 * @param {HTMLElement} container
 * @param {number} storeId
 */
export async function renderShoppingMode(container, storeId) {
  clearElement(container);

  try {
    const store = await getStore(storeId);
    if (!store) {
      showToast('Magasin introuvable', 3000, 'error');
      navigate('/');
      return;
    }

    const [items, aisles] = await Promise.all([
      getListItems(storeId),
      getAislesByStore(storeId),
    ]);

    if (items.length === 0) {
      showToast('Liste vide, ajoutez des articles d\'abord', 3000, 'error');
      navigate(`/store/${storeId}`);
      return;
    }

    const checkedCount = items.filter((i) => i.checked).length;
    const totalCount = items.length;
    const progressPct = Math.round((checkedCount / totalCount) * 100);

    const page = createElement('div', { className: 'page page-shopping' });

    page.appendChild(renderHeader({
      title: 'Mode courses',
      backPath: `/store/${storeId}`,
    }));

    // Barre de progression
    const progressBar = createElement('div', { className: 'shopping__progress' });
    const progressFill = createElement('div', { className: 'shopping__progress-fill' });
    progressFill.style.width = progressPct + '%';
    progressBar.appendChild(progressFill);

    const progressText = createElement('div', {
      className: 'shopping__progress-text',
      text: `${checkedCount}/${totalCount} articles • ${progressPct}%`,
    });

    const progressSection = createElement('div', {
      className: 'shopping__progress-section',
      children: [progressText, progressBar],
    });
    page.appendChild(progressSection);

    // Regrouper les items par rayon
    const itemsByAisle = new Map();
    for (const aisle of aisles) {
      itemsByAisle.set(aisle.id, []);
    }
    const uncategorized = [];

    for (const item of items) {
      if (item.aisleId != null && itemsByAisle.has(item.aisleId)) {
        itemsByAisle.get(item.aisleId).push(item);
      } else {
        uncategorized.push(item);
      }
    }

    const aislesContainer = createElement('div', { className: 'shopping__aisles' });

    const refresh = () => renderShoppingMode(container, storeId);

    // Trouver le premier rayon non complété pour le mettre en surbrillance
    let firstActiveFound = false;

    for (const aisle of aisles) {
      const aisleItems = itemsByAisle.get(aisle.id) ?? [];
      if (aisleItems.length === 0) continue;

      const aisleChecked = aisleItems.filter((i) => i.checked).length;
      const aisleDone = aisleChecked === aisleItems.length;
      const isActive = !aisleDone && !firstActiveFound;
      if (isActive) firstActiveFound = true;

      aislesContainer.appendChild(
        renderShoppingAisle(aisle, aisleItems, aisleChecked, aisleDone, isActive, refresh),
      );
    }

    if (uncategorized.length > 0) {
      const uncatChecked = uncategorized.filter((i) => i.checked).length;
      const uncatDone = uncatChecked === uncategorized.length;
      const isActive = !uncatDone && !firstActiveFound;

      aislesContainer.appendChild(
        renderShoppingAisle(null, uncategorized, uncatChecked, uncatDone, isActive, refresh),
      );
    }

    page.appendChild(aislesContainer);
    container.appendChild(page);

    // Pas d'auto-scroll : l'utilisateur gère son scroll lui-même
  } catch (err) {
    showToast(err.message || 'Erreur', 3000, 'error');
    navigate('/');
  }
}

/**
 * Rend une section rayon en mode courses.
 * @param {Object|null} aisle
 * @param {Array} items
 * @param {number} checkedCount
 * @param {boolean} done
 * @param {boolean} active - Premier rayon en cours
 * @param {Function} refresh
 * @returns {HTMLElement}
 */
function renderShoppingAisle(aisle, items, checkedCount, done, active, refresh) {
  const isUncategorized = aisle === null;
  const classes = [
    'shopping-aisle',
    done ? 'shopping-aisle--done' : '',
    active ? 'shopping-aisle--active' : '',
  ].filter(Boolean).join(' ');

  const section = createElement('section', { className: classes });

  const iconEl = isUncategorized ? iconPackage(18) : iconMapPin(18);
  const title = isUncategorized ? 'Sans rayon' : aisle.name;
  const badge = `${checkedCount}/${items.length}`;

  const header = createElement('div', {
    className: 'shopping-aisle__header',
    children: [
      createElement('span', { className: 'shopping-aisle__icon', children: [iconEl] }),
      createElement('h3', { className: 'shopping-aisle__title', text: title }),
      createElement('span', {
        className: `badge ${done ? 'badge--success' : 'badge--muted'}`,
        text: badge,
      }),
      done ? createElement('span', { className: 'shopping-aisle__check', children: [iconCheck(18)] }) : null,
    ].filter(Boolean),
  });

  const itemsList = createElement('div', { className: 'shopping-aisle__items' });

  // Non cochés d'abord, cochés ensuite
  const sorted = [...items].sort((a, b) => {
    if (a.checked !== b.checked) return a.checked ? 1 : -1;
    return a.name.localeCompare(b.name);
  });

  for (const item of sorted) {
    const itemEl = createElement('button', {
      className: `shopping-item ${item.checked ? 'shopping-item--checked' : ''}`,
      attrs: { type: 'button' },
      children: [
        createElement('span', {
          className: 'shopping-item__check',
          children: [item.checked ? iconCheck(18) : null].filter(Boolean),
        }),
        createElement('span', {
          className: 'shopping-item__name',
          text: item.name,
        }),
        item.quantity > 1
          ? createElement('span', {
            className: 'shopping-item__qty',
            text: `×${item.quantity}`,
          })
          : null,
      ].filter(Boolean),
      events: {
        click: async () => {
          try {
            await toggleItem(item.id);
            refresh();
          } catch (err) {
            showToast(err.message || 'Erreur', 3000, 'error');
          }
        },
      },
    });
    itemsList.appendChild(itemEl);
  }

  section.append(header, itemsList);
  return section;
}
